import { infiniteQueryOptions, queryOptions, type QueryClient } from '@tanstack/react-query';

import { UserData, UserListQuery } from 'chaika-frontend-types/frontend-cms';
import { Locale, RoleName, UserSearchField, UserStatus } from 'chaika-enums';

import { UserApi } from '@api/UserApi';
import { store } from '@store';
import { getUniqChunkedIds } from '@utils/getUniqChunkedIds';

export const DEFAULT_LIST_LIMIT = 50;

export type UserListFilters = Omit<
    UserListQuery,
    | 'locale'
    | 'ids'
    | 'emails'
    | 'mainOrganizationId'
    | 'practiceOrganizationId'
    | 'subjectInOrganizationIds'
    | 'roleNames'
    | 'queryFields'
    | 'statuses'
> & {
    ids?: string[];
    emails?: string[];
    mainOrganizationId?: string[];
    practiceOrganizationId?: string[];
    subjectInOrganizationIds?: string[];
    roleNames?: RoleName[];
    queryFields?: UserSearchField[];
    statuses?: UserStatus | UserStatus[];
    locale?: Locale;
};

type ListKeyFilters = Omit<
    UserListFilters,
    | 'ids'
    | 'emails'
    | 'mainOrganizationId'
    | 'practiceOrganizationId'
    | 'subjectInOrganizationIds'
    | 'roleNames'
    | 'queryFields'
    | 'statuses'
> & {
    ids?: readonly string[];
    emails?: readonly string[];
    mainOrganizationId?: readonly string[];
    practiceOrganizationId?: readonly string[];
    subjectInOrganizationIds?: readonly string[];
    roleNames?: readonly RoleName[];
    queryFields?: readonly UserSearchField[];
    statuses?: readonly UserStatus[];
};

function normalizeFilters({
    ids,
    emails,
    mainOrganizationId,
    practiceOrganizationId,
    subjectInOrganizationIds,
    roleNames,
    queryFields,
    statuses,
    ...filters
}: UserListFilters): ListKeyFilters {
    const statusArray = statuses === undefined ? undefined : Array.isArray(statuses) ? statuses : [statuses];

    return {
        ...filters,
        ids: ids ? [...ids].sort() : undefined,
        emails: emails ? [...emails].sort() : undefined,
        mainOrganizationId: mainOrganizationId ? [...mainOrganizationId].sort() : undefined,
        practiceOrganizationId: practiceOrganizationId ? [...practiceOrganizationId].sort() : undefined,
        subjectInOrganizationIds: subjectInOrganizationIds ? [...subjectInOrganizationIds].sort() : undefined,
        roleNames: roleNames ? [...roleNames].sort() : undefined,
        queryFields: queryFields ? [...queryFields].sort() : undefined,
        statuses: statusArray ? [...statusArray].sort() : undefined,
    };
}

export const userKeys = {
    all: ['users'] as const,
    lists: () => [...userKeys.all, 'list'] as const,
    list: (filters: UserListFilters) => [...userKeys.lists(), { filters: normalizeFilters(filters) }] as const,
    infiniteLists: () => [...userKeys.all, 'infiniteList'] as const,
    infiniteList: (filters: UserListFilters) =>
        [...userKeys.infiniteLists(), { filters: normalizeFilters(filters) }] as const,
};

export interface ListRequestParams {
    filters: UserListFilters;
    limit: number | undefined;
    offset?: number;
    locale?: Locale;
}

function buildListRequest({ filters, limit, offset, locale }: ListRequestParams) {
    const {
        ids,
        emails,
        mainOrganizationId,
        practiceOrganizationId,
        subjectInOrganizationIds,
        roleNames,
        queryFields,
        statuses,
        ...rest
    } = filters;
    const statusesParam = statuses === undefined ? undefined : Array.isArray(statuses) ? statuses.join(',') : statuses;

    return UserApi.getList({
        ...rest,
        ids: ids?.join(','),
        emails: emails?.join(','),
        mainOrganizationId: mainOrganizationId?.join(','),
        practiceOrganizationId: practiceOrganizationId?.join(','),
        subjectInOrganizationIds: subjectInOrganizationIds?.join(','),
        roleNames: roleNames?.join(','),
        queryFields: queryFields?.join(','),
        statuses: statusesParam,
        limit,
        offset,
        locale,
    });
}

export async function fetchUserListByChunks(params: ListRequestParams): Promise<UserData[]> {
    const { ids } = params.filters;

    if (!ids) {
        return buildListRequest(params);
    }

    const responses = await Promise.all(
        getUniqChunkedIds([...ids]).map(idsChunk =>
            buildListRequest({ ...params, filters: { ...params.filters, ids: idsChunk } }),
        ),
    );

    return responses.flat();
}

export const userQueryOptions = {
    list: ({ limit, locale = store.authUser.selectedLanguage, ...filters }: UserListFilters) => {
        // When ids/emails are specified the result set is implicitly bounded by their count;
        // the explicit limit is meaningless, so don't pass it.
        const effectiveLimit = filters.ids || filters.emails ? undefined : (limit ?? DEFAULT_LIST_LIMIT);

        return queryOptions({
            staleTime: Infinity,
            queryKey: userKeys.list({ ...filters, limit: effectiveLimit, locale }),
            queryFn: () => fetchUserListByChunks({ filters, limit: effectiveLimit, locale }),
        });
    },
    infiniteList: ({ limit, locale = store.authUser.selectedLanguage, ...filters }: UserListFilters) => {
        const pageSize = limit ?? DEFAULT_LIST_LIMIT;

        return infiniteQueryOptions({
            queryKey: userKeys.infiniteList({ ...filters, limit: pageSize, locale }),
            queryFn: ({ pageParam }) => buildListRequest({ filters, limit: pageSize, offset: pageParam, locale }),
            initialPageParam: 0,
            getNextPageParam: (lastPage: UserData[], allPages: UserData[][]) =>
                lastPage.length === pageSize ? allPages.length * pageSize : undefined,
        });
    },
};

export function invalidateAllUsers(queryClient: QueryClient): Promise<void> {
    return queryClient.invalidateQueries({ queryKey: userKeys.all });
}

export function removeAllUsers(queryClient: QueryClient): void {
    queryClient.removeQueries({ queryKey: userKeys.all });
}

// Synchronous cache read for imperative flows that prefetch first (fetchQuery with the
// needed ids) and then build data outside React — e.g. the entry print-form helpers.
export function getCachedUserById(queryClient: QueryClient, userId: string): UserData | undefined {
    const cachedListQueries = queryClient.getQueriesData<UserData[]>({ queryKey: userKeys.lists() });

    for (const [, users] of cachedListQueries) {
        const match = users?.find(({ id }) => id === userId);
        if (match) {
            return match;
        }
    }

    return undefined;
}

// AsyncUserSelect / AsyncUserMultiSelect resolve their closed-select labels by reading
// from `userQueryOptions.list({ ids: selectedUserIds })`. When the user just picked an
// option, the corresponding UserData is already cached under some other list query (the
// bulk autoload or the search). Copy it across so the per-ids query resolves from cache
// instead of round-tripping.
export function primeUserListByIdsCache(queryClient: QueryClient, userIds: string[]): void {
    if (userIds.length === 0) {
        return;
    }

    const targetKey = userQueryOptions.list({ ids: userIds }).queryKey;

    if (queryClient.getQueryData(targetKey)) {
        return;
    }

    const cachedUsersById = new Map<string, UserData>();
    const cachedListQueries = queryClient.getQueriesData<UserData[]>({ queryKey: userKeys.lists() });
    for (const [, users] of cachedListQueries) {
        users?.forEach(user => cachedUsersById.set(user.id, user));
    }

    const matched = userIds.map(id => cachedUsersById.get(id)).filter((user): user is UserData => user !== undefined);

    if (matched.length === userIds.length) {
        queryClient.setQueryData(targetKey, matched);
    }
}
